/**
 * <kk-cart-offer> — keeps blocks/kk-cart-offer.liquid live.
 *
 * Reads the tiers rendered by its child blocks ([data-kk-offer-tier] with data-quantity / data-reward),
 * then on every cart change (KK or Horizon, via Shopify's CartLinesUpdateEvent) updates:
 * the "2/4 packs selected" chip, the progress bar + bags, the mascot, the "You saved" pills and the hint line
 * ("Add 1 more pack to unlock ₹200 OFF" → "Woohoo! You unlocked ₹200 OFF"), and fills one slot per pack.
 * All text is written with textContent.
 */
// Shopify's standard events file is an optional extra (it keeps Horizon's own cart parts in sync).
// It is downloaded from cdn.shopify.com; if it is blocked, slow or down, everything here still works.
let standardEvents = null;
try {
  standardEvents = await Promise.race([
    import('@shopify/events'),
    new Promise((_, reject) => setTimeout(() => reject(new Error('timed out')), 3000)),
  ]);
} catch (error) {
  console.warn('[KK] Shopify standard events unavailable; cart works without syncing Horizon parts.', error);
}
const CART_LINES_UPDATE = standardEvents?.StandardEvents?.cartLinesUpdate ?? 'shopify:cart:lines-update';


class KKCartOffer extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    document.addEventListener(CART_LINES_UPDATE, (event) => this.#onCartUpdate(event), { signal });
    // KK's own cart script announces every change too, so this works even without the events file.
    document.addEventListener('kk:cart:updated', (event) => this.update(Number(event.detail?.cart?.item_count) || 0), { signal });
    // A full box: the button becomes "Proceed to checkout" (caught before KK's add-to-cart handler).
    document.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest?.('.kk-cart-offer__button');
        if (!button || !this.contains(button) || !this.hasAttribute('data-complete')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        button.setAttribute('aria-busy', 'true');
        window.location.href = this.dataset.checkoutUrl || '/checkout';
      },
      { signal, capture: true }
    );
    // The ticket notches sit exactly on the divider line, wherever the content makes it fall.
    const alignNotches = () => {
      const line = this.querySelector('.kk-cart-offer__progress');
      if (line) this.style.setProperty('--kk-co-notch-y', `${line.offsetTop + 1}px`);
    };
    this.resizeObserver = new ResizeObserver(alignNotches);
    this.resizeObserver.observe(this);
    alignNotches();
    this.update(Number(this.dataset.count) || 0);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
  }

  #onCartUpdate(event) {
    event.promise
      ?.then((result) => {
        const count = result?.cart?.totalQuantity ?? result?.detail?.itemCount;
        if (typeof count === 'number') this.update(count);
      })
      .catch(() => {});
  }

  /** @param {number} count - total quantity in the cart */
  update(count) {
    const max = Math.max(1, Number(this.dataset.max) || 4);
    const shown = Math.min(count, max);

    this.toggleAttribute('data-complete', count >= max);
    const buy = this.querySelector('.kk-cart-offer__button');
    if (buy) buy.disabled = count < max && !this.hasAttribute('data-has-items');

    // The bar fills to the number of packs; the mascot runs on the end of the fill and celebrates when the box is full.
    this.querySelector('[data-kk-pack-progress]')?.style.setProperty('--kk-pp-progress', `${Math.round((shown / max) * 100)}%`);
    const baseMood = shown >= max ? 'celebrate' : 'run';
    this.querySelectorAll('[data-kk-mascot]').forEach((mascot) => {
      mascot.dataset.baseMood = baseMood;
      // kk-cart.js shows the sad face for a moment when something was removed; do not overwrite it.
      if (!mascot.dataset.sad) mascot.dataset.mood = baseMood;
    });

    // One bag per pack in the box: fill the first `shown` of them.
    const slots = this.querySelector('[data-kk-offer-slots]');
    slots?.setAttribute('aria-label', `${shown} of ${max} packs in your bag`);
    this.querySelectorAll('[data-kk-offer-slot]').forEach((slot, index) => {
      const filled = index < shown;
      if (filled !== slot.hasAttribute('data-filled')) slot.toggleAttribute('data-filled', filled);
    });

    const chip = this.querySelector('[data-kk-offer-chip]');
    if (chip) {
      chip.textContent = this.#fill(this.dataset.chipText || '[count]/[total] packs selected', {
        count: shown,
        total: max,
      });
    }

    const tiers = [...this.querySelectorAll('[data-kk-offer-tier]')]
      .map((el) => ({ el, quantity: Number(el.dataset.quantity) || 0, reward: el.dataset.reward || '' }))
      .sort((a, b) => a.quantity - b.quantity);

    tiers.forEach((tier) => tier.el.toggleAttribute('data-reached', count >= tier.quantity));

    const hint = this.querySelector('[data-kk-offer-hint]');
    if (!hint) return;

    const next = tiers.find((tier) => count < tier.quantity);
    if (next) {
      const needed = next.quantity - count;
      hint.textContent = this.#fill(this.dataset.hintText || 'Add [count] more [packs] to unlock [reward]', {
        count: needed,
        packs: needed === 1 ? this.dataset.packSingular || 'pack' : this.dataset.packPlural || 'packs',
        reward: next.reward,
      });
    } else if (tiers.length) {
      hint.textContent = this.#fill(this.dataset.doneText || 'You unlocked [reward]', {
        reward: tiers[tiers.length - 1].reward,
      });
    } else {
      hint.textContent = '';
    }
  }

  #fill(template, values) {
    return template.replace(/\[(\w+)\]/g, (match, key) => (key in values ? String(values[key]) : match));
  }
}

if (!customElements.get('kk-cart-offer')) {
  customElements.define('kk-cart-offer', KKCartOffer);
}
